import { useQueries, UseQueryResult } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import {
  getScansOptions,
  useGetAllIntegrationsPages,
  useGetAllRepositoryIntegrationsPages,
} from '../../utils/api-client/api-hooks'
import { type ScanAggregateResponse, ToolSchema } from '../../utils/api-client/api-schemas'
import { convertIntegrationTypeToTool, isToolIntegration } from '../../utils/repository-utils'

export const useGetToolsForRepository = ({ repositoryId }: { repositoryId: string }) => {
  const { data: toolsWithIntegrations, isLoading: isToolsWithIntegrationsLoading } = useGetToolsWithIntegrations()
  const { data: toolsWithRepositoryIntegrations, isLoading: isToolsWithRepositoryIntegrationsLoading } =
    useGetToolsWithRepositoryIntegrations({ repositoryId })
  const { data: toolsWithScans, isLoading: isToolsWithScansLoading } = useGetToolsWithScans({ repositoryId })

  const tools = useMemo(
    () => new Set([...toolsWithIntegrations, ...toolsWithRepositoryIntegrations, ...toolsWithScans]),
    [toolsWithIntegrations, toolsWithRepositoryIntegrations, toolsWithScans]
  )

  return {
    data: tools,
    isLoading: isToolsWithIntegrationsLoading || isToolsWithRepositoryIntegrationsLoading || isToolsWithScansLoading,
  }
}

const useGetToolsWithScans = ({ repositoryId }: { repositoryId: string }) => {
  const queries = ToolSchema.options.map(tool =>
    getScansOptions({
      repositoryIds: [repositoryId],
      tools: [tool],
    })
  )

  const combine = useCallback(
    (results: UseQueryResult<ScanAggregateResponse>[]) => ({
      data: new Set(
        results
          .filter(
            (result): result is Extract<typeof result, { isSuccess: true }> =>
              result.isSuccess && result.data && result.data.total > 0
          )
          .map(result => result.data._embedded.items[0]!.detector)
      ),
      isLoading: results.some(result => result.isLoading),
    }),
    []
  )

  return useQueries({ queries, combine })
}

const useGetToolsWithIntegrations = () => {
  const results = useGetAllIntegrationsPages()
  const tools = results
    .flatMap(result => result.data?._embedded.items ?? [])
    .filter(isToolIntegration)
    .map(item => convertIntegrationTypeToTool(item.type))

  return {
    data: new Set(tools),
    isLoading: results.some(result => result.isLoading),
  }
}

const useGetToolsWithRepositoryIntegrations = ({ repositoryId }: { repositoryId: string }) => {
  const results = useGetAllRepositoryIntegrationsPages({ repositoryId })
  const tools = results
    .flatMap(result => result.data?._embedded.items ?? [])
    .map(item => convertIntegrationTypeToTool(item.type))

  return {
    data: new Set(tools),
    isLoading: results.some(result => result.isLoading),
  }
}
